import { useState } from "react";
import { Search, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  useVendorLookupMutation,
  useCreateConnection,
} from "@/hooks/useConnections";



// ─── New Connection Dialog ────────────────────────────────────────

export function NewConnectionDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [vendorOnEaze, setVendorOnEaze] = useState(true);
  const [lookupQuery, setLookupQuery] = useState("");
  const [selectedVendor, setSelectedVendor] = useState<{
    companyId: string;
    businessId: string;
    businessName: string;
  } | null>(null);
  const [externalName, setExternalName] = useState("");
  const [externalLegalName, setExternalLegalName] = useState("");
  const [externalTaxId, setExternalTaxId] = useState("");
  const [externalAddress, setExternalAddress] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [autoPostOnClose, setAutoPostOnClose] = useState(false);
  const [notes, setNotes] = useState("");
  const [initiatorRole, setInitiatorRole] = useState<"distributor" | "vendor">(
    "distributor",
  );

  const lookup = useVendorLookupMutation();
  const create = useCreateConnection();

  const reset = () => {
    setVendorOnEaze(true);
    setLookupQuery("");
    setSelectedVendor(null);
    setExternalName("");
    setExternalLegalName("");
    setExternalTaxId("");
    setExternalAddress("");
    setContactName("");
    setContactPhone("");
    setContactEmail("");
    setAutoPostOnClose(false);
    setNotes("");
    setInitiatorRole("distributor");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleLookup = () => {
    if (!lookupQuery.trim()) return;
    lookup.mutate(lookupQuery.trim(), {
      onSuccess: (result) => {
        if (result.onEaze && result.businessId) {
          setSelectedVendor({
            companyId: result.companyId!,
            businessId: result.businessId,
            businessName: result.businessName!,
          });
          toast.success(`Found: ${result.businessName}`);
        } else {
          toast.info("Not found on Eaze — add as external vendor instead");
          setVendorOnEaze(false);
          setExternalName(lookupQuery.trim());
        }
      },
    });
  };

  const canSubmit = vendorOnEaze
    ? !!selectedVendor
    : externalName.trim().length > 0;

  const handleSubmit = () => {
    const data: any = {
      vendorOnEaze,
      initiatorRole,
      autoPostOnClose,
      notes: notes.trim() || undefined,
      otherContactName: contactName.trim() || undefined,
      otherContactPhone: contactPhone.trim() || undefined,
      otherContactEmail: contactEmail.trim() || undefined,
    };

    if (vendorOnEaze && selectedVendor) {
      data.otherSideCompanyId = selectedVendor.companyId;
      data.otherSideBusinessId = selectedVendor.businessId;
    } else {
      data.externalVendorName = externalName.trim();
      data.externalVendorLegalName = externalLegalName.trim() || undefined;
      data.externalVendorTaxId = externalTaxId.trim() || undefined;
      data.externalVendorAddress = externalAddress.trim() || undefined;
    }

    create.mutate(data, {
      onSuccess: () => {
        toast.success("Connection created");
        handleClose();
      },
      onError: (e: any) =>
        toast.error(e?.response?.data?.message || "Failed to create"),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New connection</DialogTitle>
          <DialogDescription>
            Connect with a vendor or distributor to send and receive handshakes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* My role */}
          <div className="space-y-2">
            <Label>Your role in this connection</Label>
            <Select
              value={initiatorRole}
              onValueChange={(v) =>
                setInitiatorRole(v as "distributor" | "vendor")
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="distributor">
                  Distributor (I buy from them)
                </SelectItem>
                <SelectItem value="vendor">Vendor (I sell to them)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Vendor type toggle */}
          <div className="flex items-center justify-between p-3 border rounded-md">
            <div>
              <Label className="text-sm">Other side is on Eaze</Label>
              <p className="text-xs text-muted-foreground">
                Toggle off if they're an external partner (e.g. wholesaler).
              </p>
            </div>
            <Switch
              checked={vendorOnEaze}
              onCheckedChange={(c) => {
                setVendorOnEaze(c);
                setSelectedVendor(null);
              }}
            />
          </div>

          {vendorOnEaze ? (
            <div className="space-y-2">
              <Label>Search for business on Eaze</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Business name or email"
                  value={lookupQuery}
                  onChange={(e) => setLookupQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLookup()}
                />
                <Button
                  variant="outline"
                  onClick={handleLookup}
                  disabled={lookup.isPending || !lookupQuery.trim()}
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              {selectedVendor && (
                <div className="p-2 border rounded-md bg-accent/30 flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {selectedVendor.businessName}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedVendor(null)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Vendor display name *</Label>
                <Input
                  value={externalName}
                  onChange={(e) => setExternalName(e.target.value)}
                  placeholder="Coca-Cola Ghana"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Legal name (optional)</Label>
                <Input
                  value={externalLegalName}
                  onChange={(e) => setExternalLegalName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label>Tax ID</Label>
                  <Input
                    value={externalTaxId}
                    onChange={(e) => setExternalTaxId(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Address</Label>
                  <Input
                    value={externalAddress}
                    onChange={(e) => setExternalAddress(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          <Separator />

          {/* Contact */}
          <div className="space-y-3">
            <Label className="text-sm">Their contact (optional)</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Contact name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
              <Input
                placeholder="Phone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            </div>
            <Input
              placeholder="Email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
          </div>

          {/* Auto-post toggle */}
          <div className="flex items-center justify-between p-3 border rounded-md">
            <div>
              <Label className="text-sm">Auto-post journals on close</Label>
              <p className="text-xs text-muted-foreground">
                When a handshake closes, automatically post the journal entries.
                Leave off for owner control.
              </p>
            </div>
            <Switch
              checked={autoPostOnClose}
              onCheckedChange={setAutoPostOnClose}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || create.isPending}
          >
            {create.isPending ? "Creating..." : "Create connection"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}